/**
 * What the screen the reader is on says about the question they are about to ask.
 *
 * Three things, all derived from the path:
 *
 * - `route` — the path under `/app/`, verbatim. `anomalies/<uuid>` on an anomaly,
 *   `logbook` on the log. Sent as `metadata.route` on every turn. The engine keeps
 *   only its first segment and discards the id, so it says which part of the
 *   product somebody is in, never which record they mean.
 * - `module` — the mention vocabulary's name for that part of the product, or null
 *   on a screen that has none (home, settings at large, the demo surfaces). It is
 *   the `@Anomalies` chip.
 * - `recordId` — the record a detail screen is showing, when the path carries one.
 *   It becomes the `#ANM-234` chip once the screen has said what to call it; see
 *   `useCopilotRecord` in the shell.
 *
 * The mention vocabulary is the server's `CopilotMention.module` literal, and it
 * is narrower than the app's routes: a sensor CHART is not a mention module (only
 * a sensor is), so a chart's page offers `@Sensors` and no `#`.
 */

/** `CopilotMention.module`, in the order the `@` menu offers them. */
export const MENTION_MODULES = [
  'anomalies',
  'work_orders',
  'logbook',
  'shift_handovers',
  'sensors',
  'documents',
  'assets',
  'shifts',
]

/**
 * Path (under `/app/`) → module, and which capture group is the record id.
 *
 * First match wins, so a detail pattern sits above its list. `[^/]+` for an id
 * rather than a UUID pattern: a malformed id is the page's problem to report, and
 * the chip simply never gets a label for it.
 */
const ROUTES = [
  { pattern: /^anomalies\/([^/]+)$/, module: 'anomalies', record: 1 },
  { pattern: /^anomalies$/, module: 'anomalies' },
  { pattern: /^work-orders$/, module: 'work_orders' },
  { pattern: /^logbook\/([^/]+)$/, module: 'logbook', record: 1 },
  { pattern: /^logbook$/, module: 'logbook' },
  { pattern: /^shift-handovers\/([^/]+)$/, module: 'shift_handovers', record: 1 },
  { pattern: /^shift-handovers$/, module: 'shift_handovers' },
  { pattern: /^handovers$/, module: 'shift_handovers' },
  { pattern: /^data-fusion\/iot\/[^/]+\/sensors\/(?!new$)([^/]+)$/, module: 'sensors', record: 1 },
  { pattern: /^data-fusion\/iot(\/.*)?$/, module: 'sensors' },
  { pattern: /^sensors(\/.*)?$/, module: 'sensors' },
  { pattern: /^data-fusion\/documents$/, module: 'documents' },
  { pattern: /^settings\/shifts$/, module: 'shifts' },
  { pattern: /^shifts$/, module: 'shifts' },
  { pattern: /^settings\/hierarchy$/, module: 'assets' },
  { pattern: /^assets(\/.*)?$/, module: 'assets' },
]

/**
 * `metadata.route` for a path.
 *
 * Never empty: `CopilotTurnMetadata.route` is `min_length=1`, so a turn asked from
 * a bare `/app` would be refused. That path redirects to home anyway; `home` is
 * what it means.
 */
export function routeOf(pathname = '') {
  const route = pathname.replace(/^\/app\/?/, '').replace(/\/+$/, '').slice(0, 255)
  return route || 'home'
}

export function pageContextFor(pathname = '') {
  const route = routeOf(pathname)
  for (const entry of ROUTES) {
    const match = route.match(entry.pattern)
    if (!match) continue
    return {
      route,
      module: entry.module,
      recordId: entry.record ? match[entry.record] : null,
    }
  }
  return { route, module: null, recordId: null }
}
